/**
 * API-backed PlayedChaptersProvider
 *
 * Implements the extended PlayedChaptersProvider interface against the
 * three Dissona Playback History endpoints (see
 * specs/006-sync-played-chapters/contracts/playback-history-openapi-excerpt.json).
 *
 * Layered delivery:
 *   - Phase 2: provider skeleton + factory wiring.
 *   - US1: snapshot cache, getPlayStates GET path, setPlayState happy-path
 *     POST/DELETE.
 *   - US2 (this layer): drain orchestrator, coalescer wiring for resume
 *     positions, permanent-failure (401/403/404/422) eviction with
 *     session-scoped tracking, transient (5xx / network) retry semantics.
 *   - US3: legacy eviction + signOutCleanup.
 */

import axios from 'axios';
import { nowIso } from './offline/clockSkew';

const SNAPSHOT_KEY_PREFIX = 'playback-history-snapshot-v1';
const QUEUE_KEY_PREFIX = 'playback-sync-queue-v1';
const LEGACY_KEY_PREFIX = 'played-chapters-';

let snapshotSubResolver = () => null;

export function _setSnapshotSubResolver(resolver) {
  snapshotSubResolver = typeof resolver === 'function' ? resolver : () => null;
}

function snapshotKey(sub, fileId) {
  return `${SNAPSHOT_KEY_PREFIX}::${sub}::${fileId}`;
}

function sanitizeStates(raw) {
  const out = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const id of Object.keys(raw)) {
    const v = raw[id];
    if (Number.isInteger(v) && (v === -1 || v >= 0)) {
      if (v !== 0) out[id] = v;
    }
  }
  return out;
}

export function readSnapshot(fileId) {
  const sub = snapshotSubResolver();
  if (!sub || !fileId) return null;
  try {
    const raw = localStorage.getItem(snapshotKey(sub, fileId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      states: sanitizeStates(parsed.states),
      last_synced_at: typeof parsed.last_synced_at === 'string' ? parsed.last_synced_at : null,
      cached_at: typeof parsed.cached_at === 'string' ? parsed.cached_at : null,
    };
  } catch (e) {
    console.warn('apiPlayedChapters: readSnapshot failed', e);
    return null;
  }
}

export function writeSnapshot(fileId, payload) {
  const sub = snapshotSubResolver();
  if (!sub || !fileId || !payload) return;
  try {
    const body = {
      states: sanitizeStates(payload.states),
      last_synced_at: typeof payload.last_synced_at === 'string' ? payload.last_synced_at : null,
      cached_at: new Date().toISOString(),
    };
    localStorage.setItem(snapshotKey(sub, fileId), JSON.stringify(body));
  } catch (e) {
    console.warn('apiPlayedChapters: writeSnapshot failed', e);
  }
}

// Session-scoped: chapter ids whose write was rejected with a permanent
// 4xx since this tab was opened. The next GET drops these from the local
// snapshot if they are also missing from the server response (SC-008).
const evictedThisSession = new Set();
// Module-scoped: the legacy `played-chapters-*` cleanup runs at most once
// per tab, on the first getPlayStates call (R-4 in research.md).
let legacyEvictionDone = false;

export function _resetSessionState() {
  evictedThisSession.clear();
  legacyEvictionDone = false;
}

function evictLegacyKeysOnce() {
  if (legacyEvictionDone) return;
  legacyEvictionDone = true;
  try {
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(LEGACY_KEY_PREFIX)) toRemove.push(key);
    }
    for (const key of toRemove) localStorage.removeItem(key);
  } catch (e) {
    console.warn('apiPlayedChapters: legacy eviction failed', e);
  }
}

function removeKeysWithPrefixes(prefixes) {
  try {
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && prefixes.some((p) => key.startsWith(p))) toRemove.push(key);
    }
    for (const key of toRemove) localStorage.removeItem(key);
  } catch (e) {
    console.warn('apiPlayedChapters: cleanup failed', e);
  }
}

function requireFn(name, value) {
  if (typeof value !== 'function') {
    throw new Error(`createApiPlayedChaptersProvider: ${name} is required`);
  }
}

function requireObj(name, value, methodName) {
  if (!value || typeof value[methodName] !== 'function') {
    throw new Error(`createApiPlayedChaptersProvider: ${name} is required`);
  }
}

function authHeaders(token) {
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function isUnauthorized(err) {
  return !!(err && err.response && err.response.status === 401);
}

function buildPlaybackUrl(getApiUrl, fileId, chapterId) {
  return getApiUrl(`playback-history/projects/${fileId}/chapters/${chapterId}/playback`);
}

function buildHistoryUrl(getApiUrl, fileId) {
  return getApiUrl(`playback-history/projects/${fileId}`);
}

function mergeIntoMemory(memory, fileId, payload) {
  memory.set(fileId, {
    states: { ...payload.states },
    last_synced_at: payload.last_synced_at ?? null,
  });
}

function isPermanentStatus(status) {
  return status === 401 || status === 403 || status === 404 || status === 422;
}

function preserveOptimisticInto(target, optimisticStates, chapterIds) {
  for (const cid of chapterIds) {
    if (cid in optimisticStates) target[cid] = optimisticStates[cid];
    else delete target[cid];
  }
}

function makeEntry(fileId, chapterId, value, op) {
  return {
    file_id: fileId,
    chapter_id: chapterId,
    playback_state: value,
    op,
    // Server-anchored, not `Date.now()`: a device with a wrong clock must not
    // be able to overwrite a newer change made elsewhere (FR-015).
    locally_modified_at: nowIso(),
  };
}

export function createApiPlayedChaptersProvider(deps = {}) {
  const {
    getApiUrl,
    getAccessToken,
    onUnauthorized,
    getCurrentUserSub,
    coalescer,
    queue,
  } = deps;

  requireFn('getApiUrl', getApiUrl);
  requireFn('getAccessToken', getAccessToken);
  requireFn('getCurrentUserSub', getCurrentUserSub);
  requireObj('queue', queue, 'enqueue');
  requireObj('coalescer', coalescer, 'schedule');
  const handleUnauthorized = typeof onUnauthorized === 'function' ? onUnauthorized : () => {};

  _setSnapshotSubResolver(getCurrentUserSub);

  const memory = new Map();

  function getCachedStates(fileId) {
    if (memory.has(fileId)) return { ...memory.get(fileId).states };
    const snap = readSnapshot(fileId);
    if (!snap) return {};
    mergeIntoMemory(memory, fileId, snap);
    return { ...snap.states };
  }

  async function fetchHistory(fileId) {
    const token = getAccessToken();
    if (!token) return null;
    const response = await axios.get(buildHistoryUrl(getApiUrl, fileId), {
      headers: authHeaders(token),
    });
    return response && response.data ? response.data : null;
  }

  function entryMatches(e, target) {
    return e.file_id === target.file_id
      && e.chapter_id === target.chapter_id
      && e.locally_modified_at === target.locally_modified_at;
  }

  function applyServerPayload(fileId, data, optimisticPreserve) {
    const serverStates = sanitizeStates(data && data.playback_history);
    const last = data && typeof data.last_synced_at === 'string' ? data.last_synced_at : null;
    const merged = { ...serverStates };
    if (optimisticPreserve && optimisticPreserve.size > 0) {
      const mem = memory.get(fileId);
      if (mem) preserveOptimisticInto(merged, mem.states, optimisticPreserve);
    }
    for (const cid of Array.from(evictedThisSession)) {
      if (!(cid in serverStates)) delete merged[cid];
    }
    const payload = { states: merged, last_synced_at: last };
    mergeIntoMemory(memory, fileId, payload);
    writeSnapshot(fileId, payload);
    return merged;
  }

  function applyOptimisticState(fileId, chapterId, value) {
    const current = memory.get(fileId) || { states: {}, last_synced_at: null };
    const states = { ...current.states };
    if (value === 0) delete states[chapterId];
    else states[chapterId] = value;
    const next = { states, last_synced_at: current.last_synced_at };
    memory.set(fileId, next);
    writeSnapshot(fileId, next);
  }

  async function pushChange(fileId, chapterId, value, op) {
    const token = getAccessToken();
    if (!token) return;
    const url = buildPlaybackUrl(getApiUrl, fileId, chapterId);
    const headers = authHeaders(token);
    if (op === 'clear') {
      await axios.delete(url, { headers });
    } else {
      await axios.post(url, { playback_state: value }, { headers });
    }
  }

  function handlePushError(err, entry) {
    const status = err && err.response && err.response.status;
    if (status === 401) handleUnauthorized();
    if (isPermanentStatus(status)) {
      queue.removeMatching((e) => entryMatches(e, entry));
      evictedThisSession.add(entry.chapter_id);
      return 'permanent';
    }
    // 5xx, network errors, timeouts — leave entry in queue for the next drain.
    return 'transient';
  }

  async function drainEntry(entry) {
    try {
      await pushChange(entry.file_id, entry.chapter_id, entry.playback_state, entry.op);
      queue.removeMatching((e) => entryMatches(e, entry));
      return 'pushed';
    } catch (err) {
      return handlePushError(err, entry);
    }
  }

  async function drainQueueForFile(fileId, lastSyncedAt) {
    const myEntries = queue.list().filter((e) => e.file_id === fileId);
    const pushed = new Set();
    for (const entry of myEntries) {
      const decision = queue.decideDrain(entry, lastSyncedAt);
      if (decision === 'drop') {
        queue.removeMatching((e) => entryMatches(e, entry));
        continue;
      }
      const outcome = await drainEntry(entry);
      if (outcome === 'pushed') pushed.add(entry.chapter_id);
    }
    return pushed;
  }

  /**
   * Push every pending write, regardless of which project it belongs to.
   *
   * `getPlayStates` already drains the entries for the file it is asked about,
   * but that only ever runs when a user opens a project. Reconnecting after an
   * offline session has to flush the whole queue without waiting for the user
   * to visit each project in turn (FR-009), which is what the sync engine calls
   * on every trigger.
   */
  async function drainAll() {
    const entries = queue.list();
    let pushed = 0;
    for (const entry of entries) {
      // No `lastSyncedAt` to compare against here, so nothing is dropped as
      // stale — that decision belongs to the per-file path, which knows the
      // server's timestamp. Here the queue's own last-write-wins collapsing has
      // already reduced each chapter to a single entry.
      if ((await drainEntry(entry)) === 'pushed') pushed += 1;
    }
    return pushed;
  }

  async function getPlayStates(fileId) {
    if (!fileId) return {};
    evictLegacyKeysOnce();
    const cached = getCachedStates(fileId);
    let data;
    try {
      data = await fetchHistory(fileId);
    } catch (err) {
      if (isUnauthorized(err)) handleUnauthorized();
      return cached;
    }
    if (data == null) return cached;
    const pushed = await drainQueueForFile(fileId, data.last_synced_at);
    return applyServerPayload(fileId, data, pushed);
  }

  async function pushImmediate(fileId, chapterId, value, op) {
    const entry = makeEntry(fileId, chapterId, value, op);
    queue.enqueue(entry);
    return drainEntry(entry);
  }

  async function setPlayState(fileId, chapterId, value) {
    if (!fileId || !chapterId) return;
    applyOptimisticState(fileId, chapterId, value);
    if (value > 0) {
      // Resume positions are rate-limited via the coalescer (FR-014, SC-007).
      // The coalescer fires the first emit synchronously, so the queue gains
      // an entry on the first call without awaiting the network.
      coalescer.schedule(chapterId, value, (latest) => {
        pushImmediate(fileId, chapterId, latest, 'upsert');
      });
      return;
    }
    // -1 (mark played) and 0 (clear) bypass the coalescer for immediate UX.
    await pushImmediate(fileId, chapterId, value, value === 0 ? 'clear' : 'upsert');
  }

  async function signOutCleanup() {
    // Wipe every persisted artefact owned by this provider plus any leftover
    // legacy keys (FR-007 / SC-003). Keys are matched by prefix so per-account
    // namespacing is preserved and cross-account leakage is impossible.
    // In-memory state is reset BEFORE the prefix sweep so a queue.clear()
    // re-persist of `[]` cannot reintroduce its own key after the sweep.
    memory.clear();
    if (typeof queue.clear === 'function') queue.clear();
    removeKeysWithPrefixes([
      `${SNAPSHOT_KEY_PREFIX}::`,
      `${QUEUE_KEY_PREFIX}::`,
      LEGACY_KEY_PREFIX,
    ]);
    _resetSessionState();
  }

  // Legacy binary helpers — derived from the integer-state API.
  async function getPlayed(fileId) {
    const states = await getPlayStates(fileId);
    const result = new Set();
    for (const id of Object.keys(states)) {
      if (states[id] === -1) result.add(id);
    }
    return result;
  }

  async function markPlayed(fileId, chapterId) {
    return setPlayState(fileId, chapterId, -1);
  }

  async function markUnplayed(fileId, chapterId) {
    return setPlayState(fileId, chapterId, 0);
  }

  return {
    getPlayStates,
    setPlayState,
    drainAll,
    signOutCleanup,
    getPlayed,
    markPlayed,
    markUnplayed,
  };
}
